/* eslint-disable @typescript-eslint/no-explicit-any */
/** 错误码处理相关方法 */
import { type ClassValue, clsx } from "clsx";
import DOMPurify from "dompurify";
import saveAs from "file-saver";
import html2canvas from "html2canvas";
import { get } from "http";
import { gzip, ungzip } from "pako";
import { twMerge } from "tailwind-merge";

import { isServer } from "solid-js/web";

import {
  ACTIVITY_HOST,
  APP_ID,
  APP_SKIN,
  STATIC_FILE,
} from "@/constants/constants.ts";
import { type ErrorCode, ErrorCodes } from "@/constants/errorCodes";
import { useDevice } from "@/hooks/useDevice";
import { useUrlStorage } from "@/hooks/useUrlStorage";
import { getLocale, t } from "@/i18n";
import { copyText } from "@/lib/helper.ts";
import { shareText, shareUrl } from "@/stores/affiliateStore.ts";
import { appName } from "@/stores/configStore";
import { deviceInfo } from "@/stores/deviceStore";
import { getUserChannel, userStore } from "@/stores/userStore";
import { ActivityTypeEnum } from "@/types/compontent/base/baseEnum";

import { getToken } from "./apiUtils";
import { openAppBrowser } from "./bridge";
import { isMobile } from "./isMobile";
import { getLocalStorageItem, setLocalStorageItem } from "./localStorage";
import { sdkManager } from "./sdk/sdkManager";

//字母【用于随机字符串】
const CHARACTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
//字母+数字【用于随机字符串】
const CHARACTERS_AND_NUMBERS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

// TODO: getAppChannel
export function getAppChannel() {
  return 0;
}

//生成[min, max)范围内的随机整数
export function getRandomInteger(max: number, min = 0) {
  return Math.floor(min + Math.random() * (max - min));
}

//生成指定长度的随机字符串
export function getRandomString(length = 16, includeNumbers = true) {
  const characters = includeNumbers ? CHARACTERS_AND_NUMBERS : CHARACTERS;
  return Array.from({ length }, () =>
    characters.charAt(getRandomInteger(characters.length)),
  ).join("");
}

/**
 * 去除对象中的空值（undefined、null、空字符串），包括嵌套对象
 *
 * @param obj 需要处理的对象
 * @returns 处理后的对象，已去除空值
 */
export function removeObjEmpty<T extends object>(obj: T): Partial<T> {
  if (obj === null || typeof obj !== "object") return obj as Partial<T>;

  const result: Partial<T> = Array.isArray(obj)
    ? ([] as unknown as Partial<T>)
    : ({} as Partial<T>);

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const value = obj[key];

    if (value === undefined || value === null || value === "") continue;

    if (typeof value === "object" && value !== null) {
      const cleanedValue = removeObjEmpty(value as object);

      if (
        (Array.isArray(cleanedValue) && cleanedValue.length === 0) ||
        (!Array.isArray(cleanedValue) && Object.keys(cleanedValue).length === 0)
      ) {
        continue;
      }

      result[key as keyof T] = cleanedValue as unknown as T[keyof T];
    } else {
      result[key as keyof T] = value;
    }
  }

  return result;
}

/**
 * 根据错误码获取错误信息
 *
 * @param code 错误码
 * @param defaultMsg 默认错误信息
 * @returns 错误信息
 */
export function getErrorMessage(
  code: number | string,
  defaultMsg = "未知错误",
): string {
  const codeStr =
    typeof code === "number" || typeof code === "string" ? code.toString() : "";
  if (codeStr !== "" && codeStr in ErrorCodes) {
    return ErrorCodes[codeStr as ErrorCode];
  }
  return defaultMsg;
}

export function maskNumber(number: number | string) {
  const str = number.toString();
  if (str.length <= 4) {
    return str; // If the number has 4 or fewer digits, no masking
  }
  const start = str.slice(0, 2); // First 2 digits
  const end = str.slice(-2); // Last 2 digits
  return `${start}****${end}`; // Return formatted number
}

export function maskNumberWithStar(number: number | string) {
  const str = number.toString();
  if (str.length <= 4) {
    return str; // If the number has 4 or fewer digits, no masking
  }
  const start = str.slice(0, 2); // First 2 digits
  const end = str.slice(-2); // Last 2 digits
  return `${start}***`; // Return formatted number
}

export function isAllDigits(str: string) {
  return /^\d+$/.test(str);
}

export function maskNickname(nickname: string) {
  if (isAllDigits(nickname)) {
    return maskNumber(nickname);
  }
  return nickname;
}

export function winnerNickname(number: number | string) {
  const str = number.toString();
  if (str.length <= 4) {
    return str; // If the number has 4 or fewer digits, no masking
  }
  const start = str.slice(0, 2); // First 2 digits
  const end = str.slice(-2); // Last 2 digits
  return `${start}****${end}`; // Return formatted number
}

export function addZero(value: number): string {
  if (value < 0) {
    return `${value}`;
  }
  return value < 10 ? `0${value}` : `${value}`;
}

//剩余时间计算
export function getTimeRemaining(endTime: number, total?: number) {
  if (total == null) {
    total = Math.floor(endTime - Math.round(Date.now() / 1000));
  }
  const day = parseInt(`${total / 60 / 60 / 24}`);
  const hour = parseInt(`${(total / 60 / 60) % 24}`);
  const minute = parseInt(`${(total / 60) % 60}`);
  const second = parseInt(`${total % 60}`);
  return {
    total,
    day,
    hour,
    minute,
    second,
  };
}

/**
 * 从字符串中移除指定的子字符串
 *
 * @param {string} originalStr - 原始字符串
 * @param {string[]} substringsToRemove - 要移除的子字符串数组
 * @returns {string} - 处理后的字符串
 */
export function removeSubstrings(
  originalStr: string,
  substringsToRemove: string[],
) {
  if (!originalStr || !Array.isArray(substringsToRemove)) return originalStr;

  let result = originalStr;
  substringsToRemove.forEach((substring) => {
    // 全局替换所有匹配的子串
    result = result.split(substring).join("");
  });

  return result;
}

/**
 * 提取字符串中的数字和小数点
 *
 * @param {string} str - 原始字符串
 * @returns {string} - 处理后的字符串
 */
export function extractNumber(str: string) {
  // const match = str.match(/[\d.]+/g); // 匹配所有数字和小数点
  // if (!match) return "";
  // return match.join("").replace(/(?<=\..*)\./g, ""); // 去除多余小数点，只保留第一个
  // TODO
  const match = str.match(/[\d.]+/g);
  if (!match) return "";

  const combined = match.join("");

  // 只保留第一个小数点，其它的都去掉
  const firstDotIndex = combined.indexOf(".");
  if (firstDotIndex === -1) return combined;

  const beforeDot = combined.slice(0, firstDotIndex + 1);
  const afterDot = combined.slice(firstDotIndex + 1).replace(/\./g, "");

  return beforeDot + afterDot;
}

export function renderTimeRemainFormatFull(total: number) {
  const result = getTimeRemaining(1, total);
  const day = addZero(result.day);
  const hour = addZero(result.hour);
  const minute = addZero(result.minute);
  const second = addZero(result.second);
  let returnStr = "";
  if (total > 0) {
    if (result.day > 0) {
      returnStr = `${day}D ${hour}h:${minute}m:${second}s`;
    } else {
      returnStr = `${hour}h:${minute}m:${second}s`;
    }
  } else {
    returnStr = "";
  }
  return returnStr;
}

export function renderTimeRemainFormat(total: number) {
  const result = getTimeRemaining(1, total);
  const day = result.day;
  const hour = addZero(result.hour);
  const minute = addZero(result.minute);
  const second = addZero(result.second);
  let returnStr = "";
  if (total > 0) {
    if (day > 0) {
      returnStr = `${day}D ${hour}h:${minute}m`;
    } else {
      returnStr = `${hour}h:${minute}m:${second}s`;
    }
  } else {
    returnStr = "";
  }
  return returnStr;
}

//用户名转nfc
export function userNameToNFC(name: string) {
  if (name) {
    return name.normalize("NFC");
  }
  return name;
}

//数值转dom标签
export const renderVHtmlNumber = (
  value: string | number,
  domClass: string,
): string => {
  return `<span class="${domClass}">${value}</span>`;
};

//solid-ui
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 获取字符串中的纯数字
export function pureNumber(value: string) {
  return value.replace(/\D/g, "");
}

export function replaceParam(url: string) {
  let surl = url;
  const { general, channel } = getUserChannel();
  const uid = userStore.userInfo?.uid || 0;
  if (surl.includes("[ch]")) {
    surl = surl.replace(/\[ch\]/g, String(channel));
  }
  if (surl.includes("[ic]")) {
    surl = surl.replace(/\[ic\]/g, String(uid));
  }
  if (surl.includes("[gno]")) {
    surl = surl.replace(/\[gno\]/g, String(general));
  }
  if (surl.includes("[uid]")) {
    surl = surl.replace(/\[uid\]/g, String(uid));
  }
  if (surl.includes("[cToken]")) {
    surl = surl.replace(/\[cToken\]/g, String(getToken()));
  }

  return surl;
}

export function urlAddParam(
  srcUrl: string,
  reqParam: string,
  bIncludeHash = true,
) {
  if (!srcUrl) {
    return srcUrl;
  }
  const urlArr = srcUrl.split("#");
  let newUrl = urlArr[0];
  if (reqParam) {
    newUrl = newUrl.replace(reqParam, "");
    const pIndex = newUrl.indexOf("?");
    if (pIndex >= 0) {
      newUrl = newUrl.replace(
        "?",
        "?" + reqParam + (pIndex != newUrl.length - 1 ? "&" : ""),
      );
    } else {
      newUrl = urlArr[0] + "?" + reqParam;
    }
  }
  if (bIncludeHash) {
    const urlHash = urlArr.length > 1 ? "#" + urlArr[1] : "";
    newUrl += urlHash;
  }
  return newUrl;
}

export function getUserParam() {
  const { general, channel } = getUserChannel();
  const uid = userStore.userInfo?.uid || 0;
  const pid = userStore.userInfo?.pid_code || "";
  const str = `uid=${uid}&ch=${channel}&gno=${general}&ic=${pid}`;
  return str;
}

export function addUserParam(url: string) {
  let surl = url;
  const str = getUserParam();
  surl = urlAddParam(surl, str);
  return surl;
}

export function toOutLink(url: string, addPara = true) {
  if (!url) return false;
  if (
    url.includes("[ch]") ||
    url.includes("[ic]") ||
    url.includes("[uid]") ||
    url.includes("[gno]") ||
    url.includes("[cToken]")
  ) {
    url = replaceParam(url);
  } else {
    if (addPara && !url.toLowerCase().endsWith(".apk")) {
      url = addUserParam(url);
    }
  }
  const { getStoredParams } = useUrlStorage();
  const urlParams = getStoredParams();
  if (
    (urlParams?.params?.["mApp"] == "10" ||
      urlParams?.params?.["mApp"] == "4") &&
    deviceInfo.ios
  ) {
    openAppBrowser(url);
  } else if (
    window.ReactNativeWebView &&
    window.ReactNativeWebView.postMessage
  ) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ cmd: "open_link", params: { url } }),
    );
  } else if (window.jsBridge && window.jsBridge.postMessage) {
    //三方马甲包定制
    window.jsBridge.postMessage("openURL", url);
    //三方马甲包定制WG
    window.jsBridge.postMessage("openWindow", { url });
  } else if (sdkManager.checkIsIosJsBridgeApp()) {
    sdkManager.excucIosJsBridgeFunc(
      "openAppBrowser",
      JSON.stringify({ url: url }),
    );
  } else if (deviceInfo.app) {
    //官方APP：原生接口外跳浏览器打开
    sdkManager.openAppBrowser(url);
  } else if (window.jsbridge?.postMessage) {
    //支持flutter
    window.jsbridge.postMessage(JSON.stringify({ action: "openURL", url }));
  } else if (deviceInfo.webview) {
    //其他外部包：如果有外跳浏览器接口则浏览器打开

    if (
      window.jsbridge &&
      window.jsbridge.openAppBrowser &&
      urlParams?.params?.["mApp"]
    ) {
      window.jsbridge.openAppBrowser(url);
      return true;
    } else if (
      sdkManager.openAppBrowser &&
      typeof sdkManager.openAppBrowser == "function"
    ) {
      sdkManager.openAppBrowser(url);
      return true;
    } else if (deviceInfo.ios) {
      const winObj = handleOutLink(url);
      if (winObj) {
        return true;
      } else {
        window.open(url, "_self");
      }
      return false;
    }
  } else {
    const winObj = handleOutLink(url);
    if (winObj) {
      return true;
    } else {
      window.open(url, "_self");
    }
    return true;
  }
  return true;
}

// 过滤危险字符, 避免 XSS 注入
export const renderPurifyHtml = (html: string) => {
  return DOMPurify.sanitize(html);
};

export function renderInnerHtml(value: number | string, classes = "") {
  return `<span class='${classes}'>${value}</span>`;
}

export function capitalizeString(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

interface DownloadImageOptions {
  className: string;
  name: string;
  hide?: boolean;
}

export async function downloadImage({
  className,
  name,
  hide = false,
}: DownloadImageOptions): Promise<void> {
  // fix: https://github.com/niklasvh/html2canvas/issues/3190
  // 使用 style 标签注入，确保在 html2canvas 渲染上下文中生效
  const style = document.createElement("style");
  style.innerHTML = "img { display: unset; }";
  style.setAttribute("data-html2canvas-style", "true");
  document.body.appendChild(style);

  const doc = document.querySelector(className) as HTMLElement | null;
  // 你需要实现自己的 loading 逻辑
  // const httpLoading = useHttpLoading();
  const httpLoading = { value: false };

  if (!doc) {
    // useSysConfigStore().addErrorMessage({
    //   id: "download_img_error",
    //   msg: useNuxtApp().$t("TIP001"),
    // });
    return;
  }

  httpLoading.value = true;
  let createElement = doc;
  let offScreenContainer: HTMLDivElement | null = null;

  if (hide) {
    const clonedElement = doc.cloneNode(true) as HTMLElement;
    clonedElement.style.display = "block";
    offScreenContainer = document.createElement("div");
    offScreenContainer.style.position = "absolute";
    offScreenContainer.style.left = "-9999px";
    offScreenContainer.style.top = "0";
    offScreenContainer.appendChild(clonedElement);
    document.body.appendChild(offScreenContainer);
    createElement = clonedElement;
  }

  try {
    const canvas = await html2canvas(createElement, {
      backgroundColor: null,
      scrollX: 0,
      scrollY: 0,
      scale: 1,
      useCORS: true,
      onclone: (document: Document) => {
        // 在克隆的文档中找到并强制设置样式
        const textNodes = document.querySelectorAll(".text-div");
        textNodes.forEach((node) => {
          (node as HTMLElement).style.paddingBottom = "12px";
        });
        const registerLiner = document.querySelector(
          ".register-liner",
        ) as HTMLElement | null;
        if (registerLiner) {
          registerLiner.style.color = "#D1AB56";
          registerLiner.style.background = "none";
        }
        const registerImg = document.querySelector(
          ".register-img",
        ) as HTMLElement | null;
        if (registerImg) {
          registerImg.style.marginTop = "0px";
        }
      },
    });

    if (offScreenContainer) {
      document.body.removeChild(offScreenContainer);
    }
    // 删除 style 标签
    document.body.removeChild(style);

    canvas.toBlob((blob) => {
      httpLoading.value = false;
      if (blob) {
        saveAs(blob, `${name}.png`);
      }
    });
  } catch (err) {
    if (offScreenContainer) {
      document.body.removeChild(offScreenContainer);
    }
    // HallLog.error("html2canvas error", err);
    httpLoading.value = false;
    // useSysConfigStore().addErrorMessage({
    //   msg: useNuxtApp().$t("TIP001"),
    //   id: "download_img_error",
    // });
  }
}

// 名字脱敏
export const nameDesensitization = (name: string) => {
  if (name.length < 3) {
    return name + "****"; // 小于 2 位，直接后面加 ****
  } else if (name.length == 3) {
    return name.slice(0, 2) + "****"; // 2-3 位，只显示第一个数字
  } else {
    return name.slice(0, 2) + "****" + name.slice(-2); // 4 位及以上，前两位+****+后两位
  }
};

// 去除空格和括号中的内容
export const removeParenthesesContent = (str: string) => {
  if (!str) return str;
  return str.replace(/\s*\([^()]*\)\s*/g, "");
};
// 判断是否为图片地址
export const isImageUrl = (url: string) => {
  return (
    url.startsWith("http") ||
    url.startsWith("//") ||
    url.startsWith("data:") ||
    url.startsWith("blob:") ||
    /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i.test(url)
  );
};

export const getDefaultShareText = () => {
  return t("flip_card.reward_unlock_24h");
};

// @ts-expect-error temp
export function clickShare(
  item: {
    icon: string;
    url?: string;
    name: string;
    appUrl?: string;
    ipatype?: number;
  },
  fixShareUrl?: string,
  isType?: string | boolean,
  stopJump?: boolean,
) {
  if (item.icon.includes("more")) {
    more();
    return;
  }
  let link = fixShareUrl || shareUrl();
  if (link && !link.startsWith("http")) {
    link = window.location.origin + link;
  }
  if (isType) {
    //兼容其它活动分享参数
    if (isType == "ad_id") {
      // 兼容谷歌包分享链接
      if (link.indexOf("%23%23ad_id%23%23") != -1) {
        link = link.replace("%23%23ad_id%23%23", `${item.ipatype}`);
      } else {
        link = urlAddParam(link, `ad_id=${item.ipatype}`);
      }
    } else {
      // 兼容谷歌包分享链接
      if (link.indexOf("%23%23ipatype%23%23") != -1) {
        link = link.replace("%23%23ipatype%23%23", `${item.ipatype}`);
      } else {
        link = urlAddParam(link, `ipatype=${item.ipatype}`);
      }
    }
  }
  const shareUrlEncode = encodeURIComponent(link);
  const text = shareText() || getDefaultShareText();
  const url = item.url
    ? item.url.replace("{url}", shareUrlEncode).replace("{text}", text)
    : shareUrlEncode;
  if (!stopJump && item.appUrl) {
    const appUrl = item.appUrl
      ? item.appUrl.replace("{url}", shareUrlEncode).replace("{text}", text)
      : shareUrlEncode;
    copyText(`${appUrl}\n${text}`);
    window.location.href = appUrl;
    setTimeout(() => {
      toOutLink(url);
    }, 1500);
    return;
  }
  if (stopJump) return url;
  else toOutLink(url);
}

export async function more(url?: string, val?: string, source?: string) {
  const link = url || shareUrl();
  const shareReward = val || "R$100";
  try {
    if (navigator.share) {
      await navigator.share({
        title: t("affiliate.free_receive", { val: shareReward }),
        text: t("affiliate.visit_free_get", { link: link, val: shareReward }),
        url: link,
      });
    }
  } catch (error) {
    console.error("Error More Share:", error);
  }
}

export const formatCpfAndCnpj = (cpfOrCnpj: string, type = 0) => {
  cpfOrCnpj = cpfOrCnpj || "";
  let result = cpfOrCnpj.replaceAll(/[^0-9]/gi, "");
  if (type == 1) {
    //格式化
    if (result.length != 11 && result.length != 14) {
      return cpfOrCnpj;
    }
    if (result.length == 11) {
      //CPF 格式 645.589.777-35
      result =
        result.substring(0, 3) +
        "." +
        result.substring(3, 6) +
        "." +
        result.substring(6, 9) +
        "-" +
        result.substring(9);
    } else {
      //CNPJ 格式 00.000.000/0000-00
      result =
        result.substring(0, 2) +
        "." +
        result.substring(2, 5) +
        "." +
        result.substring(5, 8) +
        "/" +
        result.substring(8, 12) +
        "-" +
        result.substring(12);
    }
  }
  return result;
};

// 1. Gzip 压缩 JSON 数据

export const compressData = (data: any): Uint8Array => {
  const jsonStr = JSON.stringify(data);
  return gzip(new TextEncoder().encode(jsonStr));
};

// 2. Gzip 解压缩 JSON 数据

export const decompressData = (compressedData: Uint8Array): any => {
  const decompressed = ungzip(compressedData);
  const jsonStr = new TextDecoder().decode(decompressed);
  return JSON.parse(jsonStr);
};

// 3. 可靠的 base64 编码函数
export const encodeBase64 = (uint8Array: Uint8Array): string => {
  const binaryString = Array.from(uint8Array, (byte) =>
    String.fromCharCode(byte),
  ).join("");
  return btoa(binaryString);
};

// 4. 可靠的 base64 解码函数
export const decodeBase64 = (base64String: string): Uint8Array => {
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// 处理页面外跳
export const handleOutLink = (url: string): boolean => {
  if (url && url.includes("http")) {
    try {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      setTimeout(() => {
        a.click();
      }, 50);
      return true;
    } catch (error) {
      console.error("Error window.open:", error);
      return false;
    }
  }
  return false;
};

/**
 * 判断对象是否为空
 *
 * @param obj 对象
 * @returns True: 空对象 False: 非空对象
 */
export const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const getFormatImgSrc = (src: string) => {
  if (!src) {
    return src;
  }
  if (src.includes("format=auto") || src.includes("f=auto")) {
    const formatVal = "f=webp";
    return src.replace(/format=auto/, `${formatVal}`);
  }
  return src;
};
export const getFormatWidthImgSrc = (src: string, width: number | string) => {
  if (src.includes("format=auto") || src.includes("f=auto")) {
    const dpr = !isServer
      ? Math.min(Math.max(window.devicePixelRatio || 2, 2), 3)
      : 2;
    const format = "webp";
    const queries = `f=${format},w=${width},dpr=${dpr},q=80`;
    return src.replace(/format=auto/, queries);
  }
  return src;
};

/**
 * 从 HTML 标签的 data-version 属性中获取版本号
 *
 * @returns 版本号字符串，如果获取失败返回 "unknown"
 */
export function getVersionFromHtml(): string {
  try {
    // 从 html 标签获取 data-version
    const htmlElement = document.documentElement;
    const version = htmlElement.getAttribute("data-version");

    if (version) {
      return version;
    }

    // 如果 html 标签没有，尝试从 body 标签获取
    const bodyElement = document.body;
    const bodyVersion = bodyElement.getAttribute("data-version");

    if (bodyVersion) {
      return bodyVersion;
    }

    return "unknown";
  } catch (error) {
    console.warn("Failed to get version from HTML:", error);
    return "unknown";
  }
}

/**
 * 获取当前项目的版本号 优先从 HTML data-version 属性获取
 *
 * @returns 版本号字符串
 */
export function getCurrentVersion(): string {
  // 优先从环境变量获取
  if (typeof process !== "undefined" && process.env?.APP_VERSION) {
    return process.env.APP_VERSION;
  }

  // 从 HTML data-version 属性获取
  if (typeof document !== "undefined") {
    return getVersionFromHtml();
  }

  return "unknown";
}

// 提取字符串中的纯数字
export function getPureNumber(value: string): string {
  return value.replace(/[^0-9]/g, "");
}

/**
 * 判断一个值是否可以转换为数字类型
 *
 * @param value 要检查的值
 * @returns 如果可以转换为有效数字，返回 true；否则返回 false
 */
export function isNumeric(value: unknown): value is string | number {
  if (value === null || value === undefined) {
    return false;
  }

  // 如果是数字类型，直接返回 true
  if (typeof value === "number") {
    return !isNaN(value) && isFinite(value);
  }

  // 如果是字符串，检查是否可以转换为数字
  if (typeof value === "string") {
    // 空字符串或只包含空白字符的字符串返回 false
    if (value.trim() === "") {
      return false;
    }

    // 使用 Number() 转换并检查是否为有效数字
    const num = Number(value);
    return !isNaN(num) && isFinite(num);
  }

  return false;
}

/**
 * 安全地将值转换为数字，如果无法转换则返回默认值
 *
 * @param value 要转换的值
 * @param defaultValue 转换失败时的默认值，默认为 0
 * @returns 转换后的数字
 */
export function toNumber(value: unknown, defaultValue: number = 0): number {
  if (isNumeric(value)) {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }
  return defaultValue;
}

/**
 * 比较两个对象是否相等(值比较; 如: isEqual({ a: 11 }, { a: 11 }) return true)
 *
 * @param sourceObj 比较源对象
 * @param targetObj 比较的目标对象
 */
export function isEqual<T>(sourceObj: T, targetObj: T): boolean {
  // 如果是同一个引用，直接返回 true
  if (sourceObj === targetObj) {
    return true;
  }

  // 处理 null 和 undefined 情况
  if (
    sourceObj === null ||
    sourceObj === undefined ||
    targetObj === null ||
    targetObj === undefined
  ) {
    return sourceObj === targetObj;
  }

  // 处理日期对象
  if (isDate(sourceObj) && isDate(targetObj)) {
    return sourceObj.getTime() === targetObj.getTime();
  }

  // 处理正则表达式
  if (isRegExp(sourceObj) && isRegExp(targetObj)) {
    return (
      sourceObj.source === targetObj.source &&
      sourceObj.flags === targetObj.flags &&
      sourceObj.lastIndex === targetObj.lastIndex
    );
  }

  // 如果不是对象类型，直接返回 false
  if (typeof sourceObj !== "object" || typeof targetObj !== "object") {
    return false;
  }

  // 处理数组
  if (Array.isArray(sourceObj) && Array.isArray(targetObj)) {
    // 数组长度不同，直接返回 false
    if (sourceObj.length !== targetObj.length) {
      return false;
    }
    // 递归比较数组中的每个元素
    return sourceObj.every((item, index) => isEqual(item, targetObj[index]));
  }

  // 处理对象
  // 获取对象的键
  const keys1 = Object.keys(sourceObj) as Array<keyof T>;
  const keys2 = Object.keys(targetObj) as Array<keyof T>;

  // 键的数量不同，直接返回 false
  if (keys1.length !== keys2.length) {
    return false;
  }

  // 检查所有键是否存在且对应的值相等
  return keys1.every((key) => {
    // 检查键是否存在
    if (!keys2.includes(key)) {
      return false;
    }
    // 递归比较值
    return isEqual(sourceObj[key], targetObj[key]);
  });
}

/**
 * 判断传入参数是否为一个日期对象
 *
 * @param value
 */
function isDate(value: unknown): value is Date {
  return value instanceof Date;
}

/**
 * 判断传入参数是否为一个正则表达式对象
 *
 * @param value
 */
function isRegExp(value: unknown): value is RegExp {
  return value instanceof RegExp;
}

export function getOS() {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/android/i.test(ua)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return "iOS";
  }
  if (/Win/i.test(ua)) {
    return "Windows";
  }
  if (/Mac/i.test(ua)) {
    return "macOS";
  }
  if (/Linux/i.test(ua)) {
    return "Linux";
  }
  return "Unknown";
}

export function getAppType() {
  if (isMobile()) {
    return "h5";
  }
  return "pc";
}

export function getAppTypeAndOS() {
  return `${getAppType()}_${getOS()}`;
}

/**
 * 判断当前日期（加上时区偏移）是否为每月 1~7 号 或 周六/周日
 *
 * @param {number} timeOffset 时区偏移
 * @returns {boolean} 是否符合条件
 */
export function isInSpecialPeriod(timeOffset: number) {
  const utcSeconds = Math.floor(Date.now() / 1000);
  const realSeconds = utcSeconds - timeOffset;
  const realDate = new Date(realSeconds * 1000);

  const dayOfMonth = realDate.getDate(); // 1~31
  const dayOfWeek = realDate.getDay(); // 0=周日, 6=周六

  return (
    (dayOfMonth >= 1 && dayOfMonth <= 7) || dayOfWeek === 0 || dayOfWeek === 6
  );
}

/**
 * 获取当前时间在指定时区内，判断是否落入时间段数组的某个区间
 *
 * @param {number} timeOffset - 相对于UTC的时区偏移
 * @param {{ start_hour: number; end_hour: number }[]} periods - 时间段数组
 * @returns {{
 *   inRange: boolean;
 *   index: number;
 *   progress: string;
 *   isLast: boolean;
 *   currentTime: string;
 *   periodLabel: string;
 * }}
 */
export function checkTimeInPeriods(
  timeOffset: number,
  periods: any = [],
): {
  inRange: boolean;
  index: number;
  progress: string;
  isLast: boolean;
  currentTime: string;
  periodLabel: string;
} {
  // 当前 UTC 时间戳（秒）
  const utcSeconds = Math.floor(Date.now() / 1000);

  // 当前真实时间（秒）
  const realSeconds = utcSeconds - timeOffset;

  // 转换为本地 Date 对象
  const realDate = new Date(realSeconds * 1000);
  const hours = realDate.getHours();
  const minutes = realDate.getMinutes();
  const currentNumeric = hours * 100 + minutes;

  const formatTime = (num) => {
    const h = Math.floor(num / 100);
    const m = num % 100;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  // 找出当前时间所在区间
  let foundIndex = -1;
  for (let i = 0; i < periods.length; i++) {
    const { start_hour, end_hour } = periods[i];
    if (currentNumeric >= start_hour && currentNumeric <= end_hour) {
      foundIndex = i;
      break;
    }
  }

  const inRange = foundIndex !== -1;

  // progressIndex 决定进度（同之前逻辑）
  let progressIndex;
  if (inRange) {
    progressIndex = foundIndex;
  } else {
    const nextIndex = periods.findIndex((p) => p.start_hour > currentNumeric);
    if (nextIndex === -1) {
      progressIndex = periods.length - 1; // 超过最后一个区间
    } else if (nextIndex === 0) {
      progressIndex = 0; // 在第一个区间之前
    } else {
      progressIndex = nextIndex - 1; // 在两个区间之间
    }
  }

  const progress = `${progressIndex + 1}/${periods.length}`;
  const isLast = progressIndex === periods.length - 1;

  // ✅ periodLabel逻辑：在区间内返回当前区间，否则返回下一区间
  let periodLabel = "-";
  if (inRange) {
    const { start_hour, end_hour } = periods[foundIndex];
    periodLabel = `${formatTime(start_hour)} - ${formatTime(end_hour)}`;
  } else {
    const nextPeriod = periods.find((p) => p.start_hour > currentNumeric);
    if (nextPeriod) {
      periodLabel = `${formatTime(nextPeriod.start_hour)} - ${formatTime(nextPeriod.end_hour)}`;
    }
  }

  const currentTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;

  return {
    inRange,
    index: foundIndex,
    progress,
    isLast,
    currentTime,
    periodLabel,
  };
}

export function getActivityIframeUrl(
  atype: number,
  source?: string,
  params?: string,
) {
  const token = getToken();
  const subPage = getActivityIframeSubPage(atype);
  const compliance = "base";
  const protocol = window.location.protocol;

  /* prettier-ignore */ // 92 活動, 11/15 需求
  const isFirstEnter = getLocalStorageItem("activity_details_first_sync", {})?.[atype] ? 1 : 0
  const inviteeNumber = getLocalStorageItem("92_inviteeNumber", {}) ?? 0;
  let url = `${protocol}//${ACTIVITY_HOST}/${subPage}?cToken=${token}&compliance=${compliance}&locale=${getLocale()}&appID=${APP_ID}&skin=${APP_SKIN}&appName=${appName()}&first=${isFirstEnter}&invitee=${inviteeNumber}&staticFile=${STATIC_FILE}&isAfun=${isAfun()}`;
  // let url = `http://localhost:3000/${subPage}?cToken=${token}&compliance=base&locale=${getLocale()}&appID=${APP_ID}&skin=${APP_SKIN}&appName=${appName()}&first=${isFirstEnter}&invitee=${inviteeNumber}&isAfun=${isAfun()}`;

  // 访问完需要设置为 false
  setLocalStorageItem("activity_details_first_sync", {
    [atype]: false,
  });

  if (source) url += `&source=${source}`;
  if (params) url += `&${params}`;
  return url;
}

export function getActivityIframeSubPage(atype: number) {
  switch (atype) {
    case ActivityTypeEnum.gachapon:
      return "base/GachaActivity";
    case ActivityTypeEnum.firstDeposit:
      return "mx/FirstDeposit";
    case ActivityTypeEnum.roulette:
      return "base/LuckyWheel";
    case ActivityTypeEnum.sevenNewTask:
      return "base/SevenNewTask";
    case ActivityTypeEnum.flipCard:
      return "base/FlipCard";
    case ActivityTypeEnum.dailyLossBack:
      return "base/DailyLossBack";
    case ActivityTypeEnum.dailyCashbackExperience:
      return "base/DailyCashbackExperience";
    case ActivityTypeEnum.vipDayDeposit:
      return "base/VipDayDeposit";
    case ActivityTypeEnum.shakeRedPacket:
      return "base/ShakeRedPacket";
    case ActivityTypeEnum.vipWeeklyCard:
      return "base/VipWeeklyCard";
    case ActivityTypeEnum.reliefFund3:
      return "base/ReliefFund3";
    case ActivityTypeEnum.newCheckIn:
      return "base/NewCheckIn";
    case ActivityTypeEnum.cashFlop:
      return "base/InviteWheel43";
    case ActivityTypeEnum.newbieWheel:
      return "base/NewbieWheel";
    case ActivityTypeEnum.appdownload:
      return "base/AppDownload";
    case ActivityTypeEnum.superValueGiftBox:
      return "base/SuperValueGift";
    default:
      return "";
  }
}

export function isAbandonedPlat() {
  const { getParamByKey } = useUrlStorage();
  const mApp = getParamByKey("mApp");
  return mApp == "10" || mApp == "14";
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const dealString = (target: any) => {
  if (typeof target === "string") {
    return target;
  } else if (typeof target === "object") {
    return JSON.stringify(target);
  } else {
    return "";
  }
};

/**
 * 将对象的所有值转换为字符串，返回 Record<string, string> 自动剔除 null、undefined 和空字符串 "" 的键值对
 *
 * @param obj 需要转换的对象
 * @returns 转换后的 Record<string, string> 对象（已剔除空值）
 */
export const convertObjectToStringRecord = (
  obj: Record<string, unknown>,
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // 剔除 null 和 undefined
      if (value === null || value === undefined) {
        continue;
      }

      let stringValue: string;
      if (typeof value === "string") {
        stringValue = value;
      } else if (typeof value === "object") {
        stringValue = JSON.stringify(value);
      } else {
        stringValue = String(value);
      }

      // 剔除空字符串
      if (stringValue === "") {
        continue;
      }

      result[key] = stringValue;
    }
  }
  return result;
};
// 判断是否是 afun 中的 br 和 mx 站点
export const isAfun = () =>
  [...[10407, 10207, 602], ...[10405, 10206, 11748]].includes(APP_ID);

export const getAdtype = () => {
  if (isServer) {
    return "";
  }
  const url = window.location.href;
  const params = new URL(url).searchParams;
  const adtype = params.get("ad_type");
  return adtype || "";
};
